import { Link } from 'react-router-dom';
import { PublicShell } from '../components/PublicShell';
import { useEnter, useStagger } from '../lib/motion';

/**
 * Öffentliche Startseite.
 *
 * Reihenfolge bewusst so: Vertrauen, dann Funktion, dann Preis.
 *
 * Grund: die meisten Besucher kommen über den Herkunftslink im Banner einer
 * Kundenwebsite. Das sind keine Interessenten, sondern Menschen, die gerade
 * ein Einwilligungsbanner gesehen haben und wissen wollen, wer dahintersteht
 * und was mit ihren Daten passiert. Eine Preistabelle als Erstes beantwortet
 * genau diese Frage nicht.
 *
 * Alle Angaben hier sind im Projekt nachgemessen, keine Marketingzahlen.
 * Keine Zusicherung von Rechtskonformität, siehe docs/recht/README.md.
 */
export function LandingPage() {
  const heroRef = useEnter<HTMLDivElement>([]);
  const vertrauenRef = useStagger<HTMLDivElement>('[data-karte]', []);
  const preiseRef = useStagger<HTMLDivElement>('[data-preis]', []);

  return (
    <PublicShell>
      {/* ---------------------------------------------------------- Hero */}
      <section className="mx-auto w-full max-w-5xl px-4 pb-10 pt-12 sm:px-6 sm:pb-14 sm:pt-20">
        <div ref={heroRef} className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-wide text-brand-600">
            Cookie-Einwilligungen, europäisch gehostet
          </p>
          <h1 className="mt-3 text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">
            Das Banner, das Sie gerade gesehen haben
          </h1>
          <p className="mt-4 text-lg leading-relaxed text-[var(--text-muted)]">
            ilovecookies fragt auf Websites nach Ihrer Einwilligung für Cookies, blockiert Dienste bis dahin und hält
            Ihre Entscheidung nachvollziehbar fest. Ohne Ihre IP-Adresse im Klartext zu speichern.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a
              href="#vertrauen"
              className="inline-flex min-h-11 items-center rounded-xl bg-brand-600 px-5 text-[15px] font-semibold text-white transition-colors hover:bg-brand-700"
            >
              Was gespeichert wird
            </a>
            <a
              href="#preise"
              className="inline-flex min-h-11 items-center rounded-xl border border-[var(--border)] bg-[var(--surface)] px-5 text-[15px] font-semibold transition-colors hover:border-brand-400"
            >
              Für Websitebetreiber
            </a>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------ Vertrauen */}
      <section id="vertrauen" className="border-y border-[var(--border)] bg-[var(--surface)]">
        <div className="mx-auto w-full max-w-5xl px-4 py-12 sm:px-6 sm:py-16">
          <h2 className="text-2xl font-semibold tracking-tight">Was gespeichert wird, wenn Sie klicken</h2>
          <p className="mt-2 max-w-2xl text-[var(--text-muted)]">
            Ein Einwilligungsnachweis entsteht erst, wenn Sie sich entscheiden. Solange Sie nichts anklicken, wird
            nichts gespeichert.
          </p>

          <div ref={vertrauenRef} className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Karte
              titel="Keine IP im Klartext"
              text="Ihre IP-Adresse fließt nur flüchtig in einen Hashwert ein, zusammen mit einem Geheimnis, das nicht in der Datenbank liegt. Wer die Datenbank kopiert, kann daraus keine IP-Adressen zurückrechnen."
            />
            <Karte
              titel="Daten in Frankfurt am Main"
              text="Die Datenbank mit den Nachweisen läuft in einem Rechenzentrum in der EU. Die Auslieferung des Banners erfolgt über ein weltweites Netz, gespeichert wird dort nichts."
            />
            <Karte
              titel="Automatische Löschung"
              text="Nachweise werden nach einer festgelegten Frist automatisch gelöscht, voreingestellt nach 24 Monaten. Ein Log, das ewig wächst, ist kein Beleg für Sorgfalt."
            />
            <Karte
              titel="Kein Profil über Websites hinweg"
              text="Die Kennung Ihrer Einwilligung gilt nur für die eine Website. Es gibt keine Zusammenführung, keine Weitergabe an Dritte, keinen Verkauf."
            />
            <Karte
              titel="Nachweis einsehbar"
              text="Die Kennung liegt in Ihrem Browser im Cookie ilc_consent. Damit können Sie beim Websitebetreiber erfragen, was zu Ihrer Entscheidung gespeichert wurde."
            />
            <Karte
              titel="Widerruf jederzeit"
              text="Über den Link Cookie-Einstellungen im Fußbereich der Website oder das kleine Symbol am Bildschirmrand. Der Widerruf ist so einfach wie die Einwilligung."
            />
          </div>

          <p className="mt-6 text-sm text-[var(--text-muted)]">
            Gespeichert werden genau: eine Zufallskennung, Ihre Entscheidung je Zweck, Zeitpunkt, Seite ohne
            Suchparameter, Sprache, Länderkürzel, grobe Browserfamilie und der beschriebene Hashwert. Nicht
            gespeichert: IP-Adresse, vollständiger Browserkennstring, Name, E-Mail-Adresse.
          </p>
        </div>
      </section>

      {/* ------------------------------------------------------ Funktion */}
      <section className="mx-auto w-full max-w-5xl px-4 py-12 sm:px-6 sm:py-16">
        <h2 className="text-2xl font-semibold tracking-tight">Für Websitebetreiber</h2>
        <p className="mt-2 max-w-2xl text-[var(--text-muted)]">
          Ein Script-Tag im Kopf der Seite. Den Rest übernimmt das Werkzeug.
        </p>

        <div className="mt-8 grid gap-6 lg:grid-cols-3">
          <Schritt
            nummer={1}
            titel="Blockieren vor der Einwilligung"
            text="Tracking-Skripte und eingebettete Videos werden angehalten, bis eine Entscheidung vorliegt. Google Consent Mode v2 wird vorher auf abgelehnt gesetzt."
          />
          <Schritt
            nummer={2}
            titel="Cookies finden und einordnen"
            text="Ein echter Browser ruft die Website auf und protokolliert, was tatsächlich gesetzt wird. Bekannte Dienste ordnet das Werkzeug selbst ein, alles Unklare legt es zur Prüfung vor."
          />
          <Schritt
            nummer={3}
            titel="Nachweisen und widerrufen"
            text="Jede Entscheidung wird pseudonym protokolliert. Die Cookie-Erklärung auf der Datenschutzseite füllt sich aus dem letzten Scan."
          />
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-3">
          <Zahl wert="11,5 KB" label="Größe des Banners, gzip" />
          <Zahl wert="4" label="Zweckkategorien nach EU-Praxis" />
          <Zahl wert="24 Monate" label="Aufbewahrung, einstellbar" />
        </div>
      </section>

      {/* -------------------------------------------------------- Preise */}
      <section id="preise" className="border-y border-[var(--border)] bg-[var(--surface)]">
        <div className="mx-auto w-full max-w-5xl px-4 py-12 sm:px-6 sm:py-16">
          <h2 className="text-2xl font-semibold tracking-tight">Preise</h2>
          <p className="mt-2 max-w-2xl text-[var(--text-muted)]">
            Abgerechnet wird je Domain nach der Zahl der Unterseiten, die unser Scanner findet. Das ist dieselbe
            Einheit, die Sie von Cookiebot kennen, ab der zweiten Stufe zu einem deutlich niedrigeren Preis. Dazu gehört je Tarif ein
            Auslieferungsvolumen, weil auch das Ausliefern des Banners Kosten verursacht.
          </p>
          <p className="mt-2 max-w-2xl text-sm text-[var(--text-muted)]">
            Umsatzsteuer wird nach § 19 UStG nicht berechnet. Die genannten Beträge sind die Endbeträge.
          </p>

          <div ref={preiseRef} className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Preis
              name="Frei"
              preis="0 €"
              unterseiten="bis 50 Unterseiten"
              volumen="100.000 Auslieferungen"
              merkmale={['1 Domain', 'Herkunftsangabe im Banner', 'Scan monatlich']}
            />
            <Preis
              name="Klein"
              preis="7 €"
              vergleich="Cookiebot 7 €, halbes Kontingent"
              unterseiten="bis 50 Unterseiten"
              volumen="250.000 Auslieferungen"
              merkmale={['eigenes Aussehen, eigenes Logo', 'keine Herkunftsangabe', 'Scan monatlich']}
            />
            <Preis
              name="Mittel"
              preis="12 €"
              vergleich="Cookiebot 15 €"
              unterseiten="bis 350 Unterseiten"
              volumen="1 Mio Auslieferungen"
              merkmale={['alle Sprachen inbegriffen', 'Nachweis-Export', 'Scan monatlich']}
            />
            <Preis
              name="Groß"
              preis="24 €"
              vergleich="Cookiebot 30 €"
              unterseiten="bis 3.500 Unterseiten"
              volumen="3 Mio Auslieferungen"
              merkmale={['alle Sprachen inbegriffen', 'Nachweis-Export', 'Scan wöchentlich']}
              hervorgehoben
            />
            <Preis
              name="Sehr groß"
              preis="39 €"
              vergleich="Cookiebot 50 €"
              unterseiten="bis 7.000 Unterseiten"
              volumen="8 Mio Auslieferungen"
              merkmale={['alle Sprachen inbegriffen', 'Nachweis-Export', 'Scan wöchentlich']}
            />
            <Preis
              name="Maximal"
              preis="69 €"
              vergleich="Cookiebot 90 €"
              unterseiten="über 7.000 Unterseiten"
              volumen="20 Mio Auslieferungen"
              merkmale={['alle Sprachen inbegriffen', 'Nachweis-Export', 'Scan wöchentlich']}
            />
          </div>

          <div className="mt-6 rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--bg)] p-5">
            <h3 className="font-semibold">Wenn mehr ausgeliefert wird als enthalten</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-[var(--text-muted)]">
              Dann kostet jede weitere angefangene Million Auslieferungen <strong>1 €</strong>. Keine Hochstufung in
              einen anderen Tarif, keine Vertragsänderung, keine Abschaltung. Eine Million Auslieferungen kostet uns
              rund 0,30 €, der Aufschlag deckt das mit Reserve und bleibt trotzdem der kleinstmögliche Schritt.
            </p>
            <p className="mt-2 text-sm leading-relaxed text-[var(--text-muted)]">
              Beispiel: eine Seite mit 40 Unterseiten und 20 Mio Aufrufen im Monat liegt im Tarif Klein und zahlt
              5 € plus 20 € Aufschlag. Bei Cookiebot wäre sie im 7-€-Tarif, dafür ohne Scanschutz gegen die
              Zählweise. Wer viel Traffic hat, zahlt bei uns mehr. Das sagen wir vorher, statt es zu verstecken.
            </p>
          </div>

          <h3 className="mt-10 text-lg font-semibold tracking-tight">Sechs Zusagen, die wir schriftlich geben</h3>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Unterschied
              titel="Subdomains zählen nicht extra"
              text="shop. und www. derselben Domain liegen in einer Buchung. Bei Cookiebot sind das zwei volle Verträge."
            />
            <Unterschied
              titel="Kein stiller Stufensprung"
              text="Wächst Ihre Seite über eine Grenze, schreiben wir Ihnen und lassen 30 Tage Zeit. Die Rechnung steigt erst nach Ihrer Zustimmung."
            />
            <Unterschied
              titel="Offengelegte Zählweise"
              text="Gezählt wird die kanonische Adresse. Paginierte Archive, Suchergebnisse und Parameter-URLs zählen nicht als eigene Unterseite."
            />
            <Unterschied
              titel="Wir schalten nichts ab"
              text="Ein Banner, das nicht lädt, ist ein Problem für Sie, nicht für uns. Überschreitungen führen nie zur Abschaltung."
            />
            <Unterschied
              titel="Preisgarantie 24 Monate"
              text="Ihr Preis bleibt zwei Jahre ab Vertragsbeginn, auch wenn wir die Liste ändern. Monatlich kündbar, anteilige Abrechnung beim Wechsel."
            />
            <Unterschied
              titel="Mengenpreis ab fünf Domains"
              text="Ab der fünften Domain 30 Prozent Nachlass auf jede weitere. Cookiebot gibt keinen Mengenrabatt."
            />
          </div>

          <p className="mt-6 text-sm text-[var(--text-muted)]">
            Bei Jahreszahlung sind zwei Monate frei. Die sechs Zusagen stehen als{' '}
            <Link to="/agb" className="underline hover:text-[var(--text)]">
              § 5 und § 6 der AGB
            </Link>{' '}
            im Vertrag, nicht nur auf dieser Seite. Vergleichspreise: Listenpreise je Domain und Monat laut
            cookiebot.com, Stand September 2026.
          </p>
        </div>
      </section>

      {/* --------------------------------------------------- Abschluss */}
      <section className="mx-auto w-full max-w-5xl px-4 py-12 sm:px-6 sm:py-16">
        <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-6 sm:p-8">
          <h2 className="text-xl font-semibold tracking-tight">Einsetzen oder ansehen</h2>
          <p className="mt-2 max-w-2xl text-[var(--text-muted)]">
            Zugänge werden derzeit persönlich eingerichtet. Wenn Sie das Werkzeug auf Ihrer Website einsetzen möchten,
            melden Sie sich.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/login"
              className="inline-flex min-h-11 items-center rounded-xl bg-brand-600 px-5 text-[15px] font-semibold text-white transition-colors hover:bg-brand-700"
            >
              Zum Dashboard
            </Link>
            <Link
              to="/impressum"
              className="inline-flex min-h-11 items-center rounded-xl border border-[var(--border)] px-5 text-[15px] font-semibold transition-colors hover:border-brand-400"
            >
              Kontakt und Impressum
            </Link>
          </div>
          <p className="mt-5 text-xs leading-relaxed text-[var(--text-muted)]">
            ilovecookies unterstützt die Umsetzung von Anforderungen aus DSGVO und TDDDG. Es stellt die
            Rechtskonformität einer Website nicht her und sichert sie nicht zu. Die Einordnung der eingesetzten
            Dienste, die Texte des Banners und die Datenschutzerklärung verantwortet der Websitebetreiber.
          </p>
        </div>
      </section>
    </PublicShell>
  );
}

function Karte({ titel, text }: { titel: string; text: string }) {
  return (
    <div data-karte className="rounded-[var(--radius-card)] border border-[var(--border)] p-5">
      <h3 className="font-semibold">{titel}</h3>
      <p className="mt-1.5 text-sm leading-relaxed text-[var(--text-muted)]">{text}</p>
    </div>
  );
}

function Schritt({ nummer, titel, text }: { nummer: number; titel: string; text: string }) {
  return (
    <div className="flex gap-4">
      <span className="grid size-8 shrink-0 place-items-center rounded-full bg-brand-600 text-sm font-semibold text-white">
        {nummer}
      </span>
      <div>
        <h3 className="font-semibold">{titel}</h3>
        <p className="mt-1.5 text-sm leading-relaxed text-[var(--text-muted)]">{text}</p>
      </div>
    </div>
  );
}

function Zahl({ wert, label }: { wert: string; label: string }) {
  return (
    <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4">
      <p className="tabular text-2xl font-semibold text-brand-600">{wert}</p>
      <p className="mt-0.5 text-xs text-[var(--text-muted)]">{label}</p>
    </div>
  );
}

function Preis({
  name,
  preis,
  vergleich,
  unterseiten,
  volumen,
  merkmale,
  hervorgehoben,
}: {
  name: string;
  preis: string;
  vergleich?: string;
  unterseiten: string;
  volumen: string;
  merkmale: string[];
  hervorgehoben?: boolean;
}) {
  return (
    <div
      data-preis
      className={
        hervorgehoben
          ? 'rounded-[var(--radius-card)] border-2 border-brand-600 p-5'
          : 'rounded-[var(--radius-card)] border border-[var(--border)] p-5'
      }
    >
      <p className="text-sm font-semibold uppercase tracking-wide text-[var(--text-muted)]">{name}</p>
      <p className="tabular mt-1 text-3xl font-semibold">
        {preis}
        <span className="text-sm font-normal text-[var(--text-muted)]"> / Monat je Domain</span>
      </p>
      {vergleich ? (
        <p className="tabular mt-1 text-sm text-[var(--text-muted)] line-through decoration-1">{vergleich}</p>
      ) : (
        <p className="mt-1 text-sm text-[var(--text-muted)]">dauerhaft kostenfrei</p>
      )}
      <p className="mt-3 text-sm font-medium">{unterseiten}</p>
      <p className="text-sm font-medium">{volumen}</p>
      <ul className="mt-1.5 flex flex-col gap-1 text-sm text-[var(--text-muted)]">
        {merkmale.map((eintrag) => (
          <li key={eintrag}>{eintrag}</li>
        ))}
      </ul>
    </div>
  );
}

function Unterschied({ titel, text }: { titel: string; text: string }) {
  return (
    <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--bg)] p-4">
      <p className="text-sm font-semibold">{titel}</p>
      <p className="mt-1 text-sm leading-relaxed text-[var(--text-muted)]">{text}</p>
    </div>
  );
}
